import { CheckIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import { useCharge } from '../../../../hooks/useBilling';
import { formatMoneyDollars } from '../../../../utils/money';
import type { ChargeLineItem } from '../../../../services/billing';
import { useLiveOrderedLineItemIds } from './useLiveOrderedLineItemIds';
import { CREATE_PO_FIELD_LABEL_CLASS } from './createPoFieldStyles';

interface SelectLineItemsStepProps {
    chargeId: string;
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}

function LineItemRow({
    line,
    checked,
    disabled,
    onToggle
}: {
    line: ChargeLineItem;
    checked: boolean;
    disabled: boolean;
    onToggle: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onToggle}
            disabled={disabled}
            className={`flex w-full items-center gap-3 px-4 py-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
            }`}
        >
            <span
                className={`flex items-center justify-center h-4 w-4 rounded border shrink-0 ${
                    checked ? 'bg-gray-800 border-gray-800' : 'border-gray-300'
                }`}
            >
                {checked && <CheckIcon className="h-3 w-3 text-white" />}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-sm text-gray-800">{line.productName}</span>
                <span className="block truncate text-xs text-gray-500">{line.description}</span>
                {disabled && (
                    <span className="block text-[11px] text-amber-600 mt-0.5">
                        Already has a live purchase order
                    </span>
                )}
            </span>
            <span className="text-sm text-gray-600 tabular-nums shrink-0">×{line.quantity}</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0 w-20 text-right">
                {formatMoneyDollars(line.lineTotal)}
            </span>
        </button>
    );
}

export function SelectLineItemsStep({ chargeId, selectedIds, onChange }: SelectLineItemsStepProps) {
    const { charge, isLoading } = useCharge(chargeId);
    const { liveLineItemIds, isLoading: isLoadingLiveLines } = useLiveOrderedLineItemIds(
        charge?.purchaseOrders ?? []
    );

    const toggle = (id: string) => {
        onChange(
            selectedIds.includes(id)
                ? selectedIds.filter((existing) => existing !== id)
                : [...selectedIds, id]
        );
    };

    return (
        <div>
            <label className={CREATE_PO_FIELD_LABEL_CLASS}>Line items *</label>
            {charge && (
                <p className="text-xs text-gray-500 mb-2">
                    Patient:{' '}
                    <span className="font-medium text-gray-700">
                        {charge.patientName ?? 'No patient'}
                    </span>
                </p>
            )}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <OverlayScrollArea contentClassName="max-h-72" trackInsetPx={4}>
                    {isLoading || isLoadingLiveLines ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">Loading…</div>
                    ) : !charge || charge.lineItems.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                            This charge has no line items.
                        </div>
                    ) : (
                        charge.lineItems.map((line) => (
                            <LineItemRow
                                key={line.id}
                                line={line}
                                checked={selectedIds.includes(line.id)}
                                disabled={liveLineItemIds.has(line.id)}
                                onToggle={() => toggle(line.id)}
                            />
                        ))
                    )}
                </OverlayScrollArea>
            </div>
        </div>
    );
}
